import {
  BULLETIN_DEFAULT_EXPIRY_DAYS,
  BULLETIN_EXPIRY_DAYS,
  BULLETIN_HEIGHT,
  BULLETIN_NOTE_COLORS,
  BULLETIN_NOTE_MAX_CHARS,
  BULLETIN_WIDTH,
  type BulletinNoteColor,
  type BulletinNoteInput,
} from '@veriverse/shared';
import { useEffect, useState, type FormEvent, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { sendInteract } from '../net/interact.js';
import { visibleNotes } from '../state/board-logic.js';
import { useBoardStore, type OpenBulletin } from '../state/board-store.js';
import { useSessionStore } from '../state/session-store.js';

/** Where a new note goes when nobody picked a spot: roughly the middle, a little scattered. */
const defaultSpot = () => ({
  x: Math.round(BULLETIN_WIDTH / 2 - 60 + Math.random() * 40),
  y: Math.round(BULLETIN_HEIGHT / 2 - 40 + Math.random() * 40),
});

/**
 * Bulletin board (spec §6.15): sticky notes of up to 280 characters that stay up for 1, 7 or 30
 * days. Click the board to choose where a new note goes. Authors and admins can take notes down.
 */
export function BulletinPanel({ board }: { board: OpenBulletin }) {
  const { t, i18n } = useTranslation();
  const me = useSessionStore((s) => s.user);
  const notice = useSessionStore((s) => s.interactionNotice);
  const [text, setText] = useState('');
  const [color, setColor] = useState<BulletinNoteColor>('yellow');
  const [days, setDays] = useState<number>(BULLETIN_DEFAULT_EXPIRY_DAYS);
  const [spot, setSpot] = useState(defaultSpot);

  const close = () => useBoardStore.getState().closeBulletin();
  const notes = visibleNotes(board.notes, new Date());

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') useBoardStore.getState().closeBulletin();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const pickSpot = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return; // a click on a note, not the cork
    const rect = event.currentTarget.getBoundingClientRect();
    setSpot({
      x: Math.round(((event.clientX - rect.left) * BULLETIN_WIDTH) / rect.width),
      y: Math.round(((event.clientY - rect.top) * BULLETIN_HEIGHT) / rect.height),
    });
  };

  const post = (event: FormEvent) => {
    event.preventDefault();
    const body = text.trim();
    if (!body) return;
    const note: BulletinNoteInput = {
      text: body,
      color,
      x: spot.x,
      y: spot.y,
      expiresInDays: days as BulletinNoteInput['expiresInDays'],
    };
    sendInteract(board.objectId, 'post', note);
    setText('');
    setSpot(defaultSpot());
  };

  const dateFormat = new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short' });

  return (
    <div className="overlay" onClick={close}>
      <div
        className="panel bulletin"
        role="dialog"
        aria-label={t('bulletin.title')}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="board-toolbar">
          <h1>{t('bulletin.title')}</h1>
          <button type="button" className="secondary" onClick={close}>
            {t('bulletin.close')}
          </button>
        </div>
        <div className="cork" onClick={pickSpot}>
          {notes.length === 0 && <p className="muted cork-empty">{t('bulletin.empty')}</p>}
          {notes.map((note) => (
            <article
              key={note.id}
              className={`sticky sticky-${note.color}`}
              style={{
                left: `${(note.x / BULLETIN_WIDTH) * 100}%`,
                top: `${(note.y / BULLETIN_HEIGHT) * 100}%`,
              }}
            >
              <p>{note.text}</p>
              <footer>
                <span>
                  {note.authorName} · {dateFormat.format(new Date(note.createdAt))}
                </span>
                {(note.authorId === me?.id || me?.role === 'admin') && (
                  <button
                    type="button"
                    className="link"
                    aria-label={t('bulletin.remove')}
                    title={t('bulletin.remove')}
                    onClick={() => sendInteract(board.objectId, 'remove', { id: note.id })}
                  >
                    ×
                  </button>
                )}
              </footer>
            </article>
          ))}
          <span
            className="cork-spot"
            aria-hidden="true"
            style={{
              left: `${(spot.x / BULLETIN_WIDTH) * 100}%`,
              top: `${(spot.y / BULLETIN_HEIGHT) * 100}%`,
            }}
          />
        </div>
        <form className="bulletin-form" onSubmit={post}>
          <textarea
            value={text}
            maxLength={BULLETIN_NOTE_MAX_CHARS}
            rows={2}
            placeholder={t('bulletin.placeholder')}
            aria-label={t('bulletin.placeholder')}
            onChange={(event) => setText(event.target.value)}
          />
          <div className="board-group" role="radiogroup" aria-label={t('bulletin.color')}>
            {BULLETIN_NOTE_COLORS.map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={color === value}
                aria-label={t(`bulletin.colors.${value}`)}
                className={`swatch sticky-${value}${color === value ? ' selected' : ''}`}
                onClick={() => setColor(value)}
              />
            ))}
          </div>
          <label>
            {t('bulletin.expires')}{' '}
            <select value={days} onChange={(event) => setDays(Number(event.target.value))}>
              {BULLETIN_EXPIRY_DAYS.map((value) => (
                <option key={value} value={value}>
                  {t('bulletin.days', { count: value })}
                </option>
              ))}
            </select>
          </label>
          <span className="muted">
            {text.length}/{BULLETIN_NOTE_MAX_CHARS}
          </span>
          <button type="submit" disabled={!text.trim()}>
            {t('bulletin.post')}
          </button>
        </form>
        <p className="muted">{t('bulletin.hint')}</p>
        {notice && <p className="error">{t(`interact.rejected.${notice}`)}</p>}
      </div>
    </div>
  );
}
